import { useState } from 'react';
import { gramsFromFraction, type PackageFraction } from '@/engine/portions';
import { inputClass } from '@/components/ui/Field';
import { num } from '@/lib/format';
import { cn } from '@/lib/utils';

const FRACTIONS: { value: PackageFraction; label: string }[] = [
  { value: 'entero', label: 'Entero' },
  { value: 'medio', label: 'Medio' },
  { value: 'cuarto', label: 'Cuarto' },
];

interface Props {
  packageGrams: number | null | undefined;
  selected: number;
  onPick: (grams: number) => void;
  /**
   * Se llama cuando el usuario dice por fin cuánto pesa el envase. Justo
   * después se aplica, con `onPick`, la fracción que había pulsado.
   */
  onRememberWeight: (grams: number) => void;
}

/**
 * Botones Entero / Medio / Cuarto (SPEC 6.B).
 *
 * Necesitan el peso del envase. Cuando Open Food Facts no lo trae, se pregunta
 * UNA vez y se recuerda para ese producto: volver a preguntarlo cada vez sería
 * el tipo de fricción que hace abandonar la app.
 */
export function PackageFractions({ packageGrams, selected, onPick, onRememberWeight }: Props) {
  // La fracción pulsada mientras se pregunta el peso: al guardarlo se aplica
  // ESA, no el envase entero (pulsar "Medio" registraba el doble).
  const [asking, setAsking] = useState<PackageFraction | null>(null);
  const [draft, setDraft] = useState('');

  const known = packageGrams != null && packageGrams > 0;

  if (!known && asking) {
    return (
      <div className="flex flex-col gap-2 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3">
        <label htmlFor="peso-envase" className="text-sm font-medium">
          ¿Cuánto pesa el envase entero?
        </label>
        <p className="text-xs text-[var(--color-text-muted)]">
          Solo se pregunta una vez; después se recuerda para este producto.
        </p>
        <div className="flex gap-2">
          <input
            id="peso-envase"
            type="number"
            inputMode="numeric"
            min={1}
            max={10000}
            placeholder="400"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className={cn(inputClass, 'flex-1')}
          />
          <button
            type="button"
            onClick={() => {
              const grams = Number(draft);
              if (grams > 0) {
                onRememberWeight(grams);
                onPick(gramsFromFraction(grams, asking));
                setAsking(null);
              }
            }}
            disabled={!(Number(draft) > 0)}
            className="tap rounded-xl bg-[var(--color-brand-button)] px-4 text-sm font-semibold text-[var(--color-on-brand)] disabled:opacity-50"
          >
            Guardar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex gap-2">
        {FRACTIONS.map(({ value, label }) => {
          const grams = known ? gramsFromFraction(packageGrams, value) : null;
          const active = grams != null && grams === selected;
          return (
            <button
              key={value}
              type="button"
              onClick={() => (grams == null ? setAsking(value) : onPick(grams))}
              className={cn(
                'tap flex-1 flex-col rounded-xl border px-2 text-sm font-medium',
                active
                  ? 'border-[var(--color-brand)] bg-[var(--color-brand-soft)] text-[var(--color-brand-strong)]'
                  : 'border-[var(--color-border)] text-[var(--color-text-muted)]',
              )}
            >
              <span className="block">{label}</span>
              <span className="block text-[11px]">
                {grams == null ? '¿cuánto pesa?' : `${num(grams)} g`}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
